'use client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { showSoldAccountSection } from '@/components/customercenter-ui/utils/assetSaleUtils';
import { useAccount } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { useShowActiveBrandSection } from '@/frontastic/hooks/useShowActiveBrandSection';
import { TranslateText } from '@/helpers/utils/siteUtils';
import { CatalogMailingHistoryPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { DEFAULT_SITE_LANG } from '../../../../../../constants/constant';
import SendCatalogModal from '../../../modals/customerDetailAction/sendCatalog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/customAccordian/customAccordian';
import { Table, TableColumnConfig } from '../../../ui/customTable/Table';
import Loader from '../../../ui/loader';
import Pagination from '../../../utils/pagination';
import {
  CATALOG_DETAILS_HEADERS,
  CATALOG_DETAILS_KEYS,
  CATALOG_MAIL_HISTORY,
  CATALOG_RECORDS_PER_PAGE,
  PAYLOAD_QUERY,
} from '../../constants';
import { PageTexts } from '../../i18n';
import { CatalogMailHistory, ShippingAddressDetailsType } from '../../types';
import { transformMailHistory } from '../../utils';

const CustomerCategoryMailingHistory = ({
  divisionCustomerId,
  billingAddress,
  customerDetails,
}: {
  divisionCustomerId: string;
  billingAddress: ShippingAddressDetailsType;
  customerDetails: any;
}) => {
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const params = useSearchParams();
  const [tableData, setTableData] = useState([] as CatalogMailHistory[]);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { getCustomerMailingHistory } = useAccount();
  const [totalHistory, setTotalHistory] = useState(0);
  const [lastUserCatalogRequestedOn, setLastUserCatalogRequestedOn] = useState('');
  const projectSettings: ProjectSettings = useProjSettings();
  const [isSoldAccount, setIsSoldAccount] = useState(false);
  const showActiveBrandSection = useShowActiveBrandSection();

  const getMailHistory = async (pageNumber?: number) => {
    setLoading(true);
    if (PAYLOAD_QUERY.OMS_CUSTOMER_ID && PAYLOAD_QUERY.DIVISION_CODE && PAYLOAD_QUERY.CUSTOMER_ID) {
      const payload = {
        omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID),
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID),
        pageNumber: pageNumber ?? currentPage,
        pageSize: CATALOG_RECORDS_PER_PAGE,
      } as CatalogMailingHistoryPayload;
      await getCustomerMailingHistory(payload)
        .then((response) => {
          setLastUserCatalogRequestedOn(response?.lastCatalogRequestedOn ?? '');
          if (response?.MailingRecords) {
            const tarnsformedData = transformMailHistory(response);
            setTableData(tarnsformedData);
            setTotalHistory(response?.quantity);
          }
        })
        .catch(() => setLoading(false));
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const tableHeaders: TableColumnConfig<any>[] = [
    { key: CATALOG_DETAILS_KEYS.CATALOG_ID, header: CATALOG_DETAILS_HEADERS.CATALOG_ID },
    { key: CATALOG_DETAILS_KEYS.MAIL_DATE, header: CATALOG_DETAILS_HEADERS.MAIL_DATE },
    { key: CATALOG_DETAILS_KEYS.PROMO_CODE, header: CATALOG_DETAILS_HEADERS.PROMO_CODE },
    {
      key: CATALOG_DETAILS_KEYS.CATALOG_EXPIRATION_DATE,
      header: CATALOG_DETAILS_HEADERS.CATALOG_EXPIRATION_DATE,
    },
  ];

  const onPageChange = async (page: number) => {
    setCurrentPage(page);
    await getMailHistory(page);
  };

  useEffect(() => {
    const isSoldAccount = customerDetails?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.isSoldAccount ?? false;
    setIsSoldAccount(isSoldAccount);
  }, [customerDetails]);

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full rounded-md border border-foggyGray px-5 py-1 shadow-lg"
      onValueChange={(value) => {
        if (value === CATALOG_MAIL_HISTORY) {
          getMailHistory();
        }
      }}
    >
      <AccordionItem value={CATALOG_MAIL_HISTORY} className="border-none">
        <AccordionTrigger className="text-lg font-bold hover:no-underline">{Texts.catalogMailHistory}</AccordionTrigger>
        <AccordionContent className="pb-5">
          <section className="mb-2 flex items-center">
            <section className="mr-auto flex items-center gap-2">
              <span className="font-bold">{CATALOG_DETAILS_HEADERS.LAST_REQUESTED_DATE}: </span>{' '}
              {lastUserCatalogRequestedOn}
            </section>
            {!showSoldAccountSection(projectSettings, isSoldAccount) && showActiveBrandSection && (
              <button
                className="h-[30px] rounded-md border-[1px] border-foggyGray px-3 py-1 text-sm font-medium"
                onClick={() => setOpenModal(true)}
              >
                {Texts.sendCatalog}
              </button>
            )}
            {openModal && (
              <SendCatalogModal
                Texts={Texts}
                divisionCustomerId={divisionCustomerId}
                billingAddress={billingAddress}
                open={openModal}
                setOpen={setOpenModal}
              />
            )}
          </section>
          {loading && <Loader />}

          <Table columns={tableHeaders} data={tableData} />
          {!loading && tableData?.length === 0 ? 'No records found' : ''}
          {totalHistory > 5 && (
            <section className="mt-4 flex justify-end">
              <Pagination
                currentPage={currentPage}
                itemsPerPage={CATALOG_RECORDS_PER_PAGE}
                totalItems={totalHistory}
                onPageChange={onPageChange}
              ></Pagination>
            </section>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default CustomerCategoryMailingHistory;
